import {
  addDays,
  differenceInCalendarDays,
  format,
  isSameMonth,
  isSameYear,
  parseISO,
} from 'date-fns';

// Operations works in plain "YYYY-MM-DD" ops-zone dates (graphy's
// `meta.today` and `schedule.date`). parseISO on a bare date gives local
// midnight, which is all these labels and steps need: no instant is ever
// built from them, so the browser's zone cannot shift a day.

/** "Jul 16, Thu" (UI spec §6, §8) */
export const dayLabel = (date: string): string =>
  format(parseISO(date), 'MMM d, EEE');

/** "Jul 16" */
export const shortDayLabel = (date: string): string =>
  format(parseISO(date), 'MMM d');

export const shiftDay = (date: string, days: number): string =>
  format(addDays(parseISO(date), days), 'yyyy-MM-dd');

/** "YYYY-MM" */
export const monthOf = (date: string): string => date.slice(0, 7);

/** "Jul 2026" (UI spec §7) */
export const monthTitle = (month: string): string =>
  format(parseISO(`${month}-01`), 'MMM yyyy');

/** Inclusive number of days from `start` to `end`. */
export const dayCount = (start: string, end: string): number =>
  differenceInCalendarDays(parseISO(end), parseISO(start)) + 1;

/** Every date from `start` to `end` inclusive ("YYYY-MM-DD"); `[start]` alone without an end. */
export const rangeDates = (start: string, end: string | null): string[] => {
  if (end === null || end <= start) return [start];
  const out: string[] = [];
  for (let d = start; d <= end; d = shiftDay(d, 1)) out.push(d);
  return out;
};

/** Right-panel heading: "Jul 16, Thu" or "Jul 20, Mon – Jul 23, Thu · 4 days". */
export const panelHeading = (start: string, end: string | null): string =>
  end === null || end === start
    ? dayLabel(start)
    : `${dayLabel(start)} – ${dayLabel(end)} · ${dayCount(start, end)} days`;

/**
 * Toast date: "Jul 20", "Jul 20 – 23", "Jul 30 – Aug 2" (UI spec §10), with
 * the year added when the range crosses one.
 */
export const toastDateLabel = (start: string, end?: string | null): string => {
  if (!end || end === start) return shortDayLabel(start);
  const s = parseISO(start);
  const e = parseISO(end);
  if (!isSameYear(s, e)) {
    return `${format(s, 'MMM d, yyyy')} – ${format(e, 'MMM d, yyyy')}`;
  }
  return isSameMonth(s, e)
    ? `${format(s, 'MMM d')} – ${format(e, 'd')}`
    : `${format(s, 'MMM d')} – ${format(e, 'MMM d')}`;
};

/**
 * "Eastern Time": the zone every time on the page is in (BR-05). The
 * generic name stays right across a DST change inside the month shown.
 */
export const zoneLabel = (timeZone: string, at: Date = new Date()): string => {
  const name = (style: 'longGeneric' | 'long') =>
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: style as Intl.DateTimeFormatOptions['timeZoneName'],
    })
      .formatToParts(at)
      .find((part) => part.type === 'timeZoneName')?.value;
  try {
    return name('longGeneric') ?? timeZone;
  } catch {
    // Older engines without `longGeneric`.
    return name('long') ?? timeZone;
  }
};
